import { cn } from '@neo/lib/utils'
import { Tooltip } from '@neo/primitives/tooltip'
import { Lock } from 'lucide-react'
import { ProductTag } from 'v2/shared/onboarding/product-tag'
import type { BuildChoice, BuildOption, Framework, FrameworkOption } from './wizard-data'

const OptionBadge = ({ label, tone, tooltip }: NonNullable<BuildOption['badge']>) => {
  // Only restrictions ('enterprise') carry the lock.
  const pill = (
    <ProductTag
      label={label}
      icon={tone === 'enterprise' && <Lock className="h-2.5 w-2.5" strokeWidth={2} />}
    />
  )
  // A span trigger, since the tag sits inside the option's <button>.
  return tooltip ? <Tooltip content={tooltip}>{pill}</Tooltip> : pill
}

// Looks like neo's RadioGroupItem but can't be one: it sits inside a <button>,
// and Radix's item is a button too.
const Radio = ({ selected }: { selected: boolean }) => (
  <span className="flex h-4 w-4 shrink-0 items-center justify-center self-center rounded-full border border-primary transition-colors">
    {selected && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
  </span>
)

// The card border lives in StepBuild, so a follow-up panel can share the block.
export const BuildCard = ({
  option,
  selected,
  expanded,
  onSelect,
}: {
  option: BuildOption
  selected: boolean
  // A follow-up panel renders directly below, so the bottom padding shrinks.
  expanded: boolean
  onSelect: (value: BuildChoice) => void
}) => {
  // Keyboard users can't hover the tooltip, so its text is also the button's
  // accessible description, rendered outside the button.
  const descriptionId = option.badge?.tooltip
    ? `onboarding-build-${option.value}-badge-note`
    : undefined
  return (
    <>
      <button
        type="button"
        onClick={() => onSelect(option.value)}
        // A toggle, not a radio: clicking the selected option clears it.
        aria-pressed={selected}
        aria-expanded={expanded}
        aria-describedby={descriptionId}
        data-testid={`onboarding-build-${option.value}`}
        className={cn(
          'flex w-full items-start gap-3 px-4 pt-4 text-left',
          expanded ? 'pb-2' : 'pb-4',
        )}
      >
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          {/* min-h-5 keeps descriptions aligned on cards with and without a badge. */}
          <span className="flex min-h-5 flex-wrap items-center gap-2">
            <span className="text-p-600 text-foreground">{option.title}</span>
            {option.badge && <OptionBadge {...option.badge} />}
          </span>
          <span className="text-s-400 text-secondary">{option.description}</span>
        </span>
        <Radio selected={selected} />
      </button>
      {option.badge?.tooltip && (
        <span id={descriptionId} className="sr-only">
          {option.badge.tooltip}
        </span>
      )}
    </>
  )
}

export const FrameworkPill = ({
  option,
  selected,
  onSelect,
}: {
  option: FrameworkOption
  selected: boolean
  onSelect: (value: Framework) => void
}) => (
  <button
    type="button"
    onClick={() => onSelect(option.value)}
    // aria-pressed is the only selection signal a screen reader gets.
    aria-pressed={selected}
    data-testid={`onboarding-framework-${option.value}`}
    className={cn(
      'group flex w-fit items-center gap-2 rounded-lg border bg-background px-3 py-2 text-left transition-colors',
      selected ? 'border-primary' : 'cursor-pointer border-st2 hover:border-st1',
    )}
  >
    <Radio selected={selected} />
    {/* text-secondary, not tertiary: tertiary fails AA contrast for a label. */}
    <span
      className={cn(
        'whitespace-nowrap text-s-500 transition-colors',
        selected ? 'text-foreground' : 'text-secondary group-hover:text-foreground',
      )}
    >
      {option.label}
    </span>
  </button>
)
